import EmptyCart from '@/components/Cart/EmptyCart'
import FixedBottomContainer from '@/components/FixedBottomContainer'
import Loading from '@/components/Loading'
import WifButton from '@/components/WifButton'
import WifModal from '@/components/WifModal'
import { notify, WifNotification } from '@/components/WifNotification'
import SimplifiedLayout from '@/layouts/SimplifiedLayout'
import formatCurrency from '@/lib/formatCurrency'
import clsx from 'clsx'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Image from 'next/image'
import Link from 'next/link'
import { CartContext } from 'providers/CartProvider'
import React, { useEffect } from 'react'
import { useDebounce } from 'react-use'
import { CartItem } from 'types'

const CartPage: React.FC<{ from: string }> = ({
  from,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { t } = useTranslation()
  const {
    removeItem,
    parsedCart,
    processCheckout,
    updateCartItem,
    isProcessingCheckout,
    isLoading: isCartLoading,
  } = React.useContext(CartContext)

  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false)
  const [itemIdToDelete, setItemIdToDelete] = React.useState<string>('')

  const total = parsedCart?.reduce((acc: number, curr: any) => acc + curr.price * curr.quantity, 0)

  const totalQuantity = parsedCart?.reduce((acc: number, curr: any) => acc + curr.quantity, 0)

  if (isLoading || isProcessingCheckout) return <Loading />

  return (
    <SimplifiedLayout browserTitle='Cart - Wifkain' title={t('cart:Cart_Nav_Title')} from={from}>
      <div className='px-4 pt-3'>
        {!parsedCart || parsedCart.length < 1 ? (
          <EmptyCart />
        ) : (
          <ul>
            {parsedCart.map((item: CartItem, index: number) => {
              return (
                <LineItem
                  isLoading={isLoading || isCartLoading}
                  key={index}
                  quantity={item.quantity}
                  productName={item.productName!}
                  variantName={item.variantName}
                  price={item.price}
                  imageUrl={item.imageUrl}
                  index={index}
                  onQuantityChange={async (qty: number) => {
                    await updateCartItem(item, qty)
                  }}
                  onDeleteItem={() => {
                    setItemIdToDelete(item.variantId)
                    setIsModalOpen(true)
                  }}
                  handle={item.productHandle}
                />
              )
            })}
          </ul>
        )}
      </div>

      {parsedCart && parsedCart.length > 0 && (
        <FixedBottomContainer>
          <CartAction
            total={total}
            totalQuantity={totalQuantity}
            onClick={processCheckout}
            isLoading={isCartLoading}
          />
        </FixedBottomContainer>
      )}

      {isModalOpen && (
        <WifModal
          id='delete-line-item'
          bodyText={t('cart:Delete_Modal_Text')}
          mainActionText={t('cart:Delete_Modal_Confirm')}
          secondaryActionText={t('common:Word_No')}
          onClose={() => setIsModalOpen(false)}
          onMainAction={async () => {
            await removeItem(itemIdToDelete)
            notify({
              type: 'error',
              text: t('cart:Success_Delete_Item'),
            })
            setIsModalOpen(false)
          }}
        />
      )}
      <WifNotification />
    </SimplifiedLayout>
  )
}

interface LineItemProps {
  quantity: number
  productName: string
  variantName: string
  price: number
  imageUrl: string
  index: number
  isLoading: boolean
  onQuantityChange: (qty: number) => void
  onDeleteItem: () => void
  handle: string
}

const LineItem: React.FC<LineItemProps> = ({
  quantity,
  productName,
  variantName,
  price,
  imageUrl,
  index,
  isLoading,
  onQuantityChange,
  onDeleteItem,
  handle,
}) => {
  const [adjustQty, setAdjustQty] = React.useState(quantity)
  const canDecrement = adjustQty > 1

  const queueIncrement = () => {
    setAdjustQty(adjustQty + 1)
  }

  const queueDecrement = () => {
    if (!canDecrement) return
    setAdjustQty(adjustQty - 1)
  }

  useDebounce(
    async () => {
      if (adjustQty === quantity) return
      await onQuantityChange(adjustQty)
    },
    200,
    [adjustQty]
  )

  useEffect(() => setAdjustQty(quantity), [quantity])

  return (
    <li className='py-[14px] px-3 mb-4 rounded-2xl border border-[#F2F2F2] shadow-[0px_7px_14px_0px_rgba(170,113,25,0.06)]'>
      <div className='flex sm:flex-nowrap gap-2 flex-wrap'>
        <div className='w-[86px] h-[86px] overflow-hidden rounded-[10px] flex-none bg-wif-grey'>
          <Link href={`/product/${handle}`}>
            <Image
              src={imageUrl || '/icons/ic-img-placeholder.svg'}
              alt='Product name'
              width={86}
              height={86}
            />
          </Link>
        </div>
        <div className='flex flex-col flex-grow justify-between items-start text-sm py-1'>
          <Link href={`/product/${handle}`}>
            <span
              data-test={`cart-product-name-label-${index}`}
              className='text-wif-neutral-40 cursor-pointer'
            >
              {productName}
            </span>
          </Link>
          <span data-test={`cart-product-variant-label-${index}`} className='text-wif-neutral-70'>
            {variantName}
          </span>
          <span data-test={`cart-product-name-price-${index}`} className='mt-3 font-bold'>
            {formatCurrency(price)}
          </span>
        </div>
        <div className='text-right w-full flex flex-nowrap basis-1/4 items-start justify-end ml-auto'>
          <button className='w-5 h-5 mr-1 flex-none' onClick={() => onDeleteItem()}>
            <Image src='/icons/ic-trash.svg' alt='delete' width={20} height={20} />
          </button>
        </div>
      </div>
      <div className='flex flex-nowrap justify-between mt-4 pt-[10px] border-t border-light-grey text-sm'>
        <div className='flex flex-col'>
          <span className='font-light text-wif-neutral-70'>Subtotal</span>
          <span
            data-test={`cart-product-subtotal-label-${index}`}
            className='font-extrabold text-wif-orange '
          >
            {formatCurrency(price * adjustQty)}
          </span>
        </div>

        <div className='flex flex-nowrap gap-5 justify-center items-center text-right'>
          <QuantityModifierButton
            index={index}
            type='minus'
            onClick={() => queueDecrement()}
            disabled={isLoading || adjustQty === 1}
          />
          <span data-test={`cart-product-quantity-label`}>{adjustQty}</span>
          <QuantityModifierButton
            index={index}
            type='plus'
            onClick={() => queueIncrement()}
            disabled={isLoading}
          />
        </div>
      </div>
    </li>
  )
}

const QuantityModifierButton: React.FC<{
  index: number
  onClick: (qty: number) => void
  type: 'plus' | 'minus'
  disabled: boolean
}> = ({ index, onClick, type, disabled }) => {
  return (
    <button
      data-test={`cart-plus-button-${index}}`}
      className={clsx(
        'relative rounded-full w-[30px] h-[30px] shadow-[0px_4px_14px_0px_rgba(126,126,126,0.2)] transition-colors',
        'disabled:bg-wif-neutral-60 bg-black hover:bg-wif-orange'
      )}
      disabled={disabled}
      onClick={() => {
        type === 'plus' ? onClick(1) : onClick(-1)
      }}
    >
      <Image
        src={`/icons/ic-${type}.svg`}
        alt={type === 'plus' ? 'increase quantity' : 'decrease quantity'}
        width={30}
        height={30}
      />
    </button>
  )
}

const CartAction: React.FC<{
  totalQuantity: number
  total: number
  isLoading: boolean
  onClick: () => void
}> = ({ totalQuantity, total, isLoading, onClick }) => {
  const { t } = useTranslation()
  const buttonText = `${t('common:Word_Checkout')} (${totalQuantity})`
  return (
    <div className='flex flex-nowrap gap-20'>
      <div className='flex flex-col justify-between'>
        <span className='font-normal text-sm'>Total</span>
        <span className='font-extrabold text-wif-orange'>{formatCurrency(total)}</span>
      </div>
      <WifButton
        id='cart-checkout-button'
        text={buttonText}
        handleClick={onClick}
        disabled={isLoading}
      />
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ locale, req }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale!, ['common', 'navbar', 'cart'])),
      from: req.headers.referer || '',
    },
  }
}

export default CartPage
